import { faArrowAltCircleRight } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Text } from '@mantine/core'
import { translate } from 'i18n/i18n'
import type { FC } from 'react'
import React, { useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { setTokenAndPushRoute } from 'utils/setTokenAndPushRoute'
import { z } from 'zod'
import type { InputPropsType } from './AuthenticationForm'
import { AuthenticationForm } from './AuthenticationForm'
import { useToast } from './AuthenticationForm/Toast/useToast'
import { appMetaDataMaker } from './_utils/appMetaMaker'
import type { TAuthRequestFailure } from './_utils/getAuthRequestFailure'
import { getAuthRequestFailure } from './_utils/getAuthRequestFailure'

const getValidationSchema = () =>
  z.object({
    email: z
      .string()
      // required to keep the submit button enabled for a pasted trailing space; it
      // does not reach the request body, which handleSubmit trims. both are needed
      .trim()
      .min(1, {
        message: translate.phrases.validation('{{label}} is required', {
          label: translate.phrases.banyanApp('Email'),
        }),
      })
      .email({
        message: translate.phrases.validation('{{label}} is invalid', {
          label: translate.phrases.banyanApp('Email'),
        }),
      }),
    password: z.string().min(6, {
      message: translate.phrases.validation('{{label}} must be at least {{count}} characters', {
        label: translate.phrases.banyanApp('Password'),
        count: 6,
      }),
    }),
  })

export const Login: FC<{ setCurrentFormType: React.Dispatch<React.SetStateAction<string>> }> = ({
  setCurrentFormType,
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const { showToast, Toast } = useToast({ durationMillisecond: 3000, type: 'error' })

  const inputProps: InputPropsType[] = [
    {
      placeholder: translate.phrases.banyanApp('Email'),
      key: 'email',
      initialValue: '',
      isPasswordInput: false,
    },
    {
      placeholder: translate.phrases.banyanApp('Password'),
      key: 'password',
      initialValue: '',
      isPasswordInput: true,
    },
  ]

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true)

    try {
      const response = await apiFetch({
        url: '/login',
        // the schema's .trim() only reaches validation: mantine's zodResolver keeps
        // the errors and discards the parsed value, so trim what we actually send
        body: { ...values, email: values.email.trim(), appMeta: appMetaDataMaker() },
      })

      const token = response['x-token']

      if (token) {
        const redirectUri = new URLSearchParams(window.location.search).get('redirectUri')

        setTokenAndPushRoute(token, redirectUri ? decodeURI(redirectUri) : '/')
      }
    } catch (error) {
      const messages: Record<TAuthRequestFailure, string> = {
        rateLimited: translate.phrases.banyanApp('Too many attempts. Please wait a minute and try again.'),
        rejected: translate.phrases.banyanApp(
          "Your email and password combination don't seem to match. Please try again.",
        ),
        unreachable: translate.phrases.banyanApp('Please check your internet connection and try again'),
        unknown: translate.phrases.banyanApp('Sorry… Something went wrong.'),
      }

      showToast(messages[getAuthRequestFailure(error)])
    }

    setLoading(false)
  }

  return (
    <AuthenticationForm
      header={
        <>
          {Toast}
          <Text fz="24px" fw={300}>
            {translate.phrases.banyanApp('Hello again!')}
          </Text>
          <Text fz="14px" fw={400}>
            {translate.phrases.banyanApp('Please login below')}
          </Text>
        </>
      }
      inputProps={inputProps}
      validationSchema={getValidationSchema()}
      buttonText={translate.phrases.banyanApp('Login')}
      buttonRightIcon={<FontAwesomeIcon icon={faArrowAltCircleRight} />}
      anchorText={translate.phrases.banyanApp('Forgot Password?')}
      onAnchorPress={() => setCurrentFormType('forgotPassword')}
      // TODO: type this better, maybe a generic on the component
      onSubmit={(values) => handleSubmit(values as { email: string; password: string })}
      submitLoading={loading}
    />
  )
}
